import { useCallback, useContext } from 'react';

import { AmplitudeExperimentsContext } from '@/components/AmplitudeExperimentsProvider/Context';

export const useAmplitudeExperiments = () => {
    return useContext(AmplitudeExperimentsContext);
};

export const useAmplitudeFeatureFlag = (featureFlagKey: string, featureFlagVariant: string) => {
    const { client, overriddenFlagKeys, variants } = useAmplitudeExperiments();

    const shouldExposeFeature = Boolean(variants[featureFlagKey]?.value);
    // dev-tools overrides (forceExperiment, Cmd+/ cookies) are already merged into `variants`
    const isShowingFeature = variants[featureFlagKey]?.value === featureFlagVariant;
    const isVariantOverridden = Boolean(overriddenFlagKeys?.includes(featureFlagKey));

    // Returns a cleanup that cancels the pending exposure. The forceExperiment override is applied
    // post-mount (one render after the first), so the initial render schedules exposure before
    // isVariantOverridden flips true. Returning the cleanup lets the effect cancel that already-scheduled
    // exposure when the override re-render runs — otherwise the 1s timer fires and pollutes the data.
    const trackFeatureFlag = useCallback(() => {
        if (process.env.NODE_ENV === 'test') {
            // Don't track exposure in tests
            return;
        }

        // forceExperiment-pinned flags must not fire exposure — forced devices would pollute experiment data
        if (isVariantOverridden) {
            return;
        }

        if (!client) {
            console.error('useAmplitudeFeatureFlag - client is not initialized');
            return;
        }

        // track user exposure to the experiment if they are in it + the test is running in amplitude
        if (shouldExposeFeature) {
            // Wait for identify in AmplitudeProvider.tsx to be called
            const exposureTimeout = setTimeout(() => {
                client.exposure(featureFlagKey);
            }, 1000);

            return () => clearTimeout(exposureTimeout);
        }
    }, [client, featureFlagKey, isVariantOverridden, shouldExposeFeature]);

    return {
        isShowingFeature,
        trackFeatureFlag,
    };
};
